import { useEffect, useState, createContext, useContext } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getMsalInstance, isAzureAdConfigured, loginRequest } from './msalConfig';
import { initializeTeams, isRunningInTeams, getTeamsSsoToken } from './teamsAuth';
import LoginPage from '../pages/LoginPage';

// Context for Teams state
const TeamsContext = createContext({ isInTeams: false, teamsSsoToken: null });
export const useTeams = () => useContext(TeamsContext);

/**
 * Component that requires authentication
 * Shows login page if not authenticated
 * Supports Teams SSO token authentication
 */
function RequireAuth({ children, teamsSsoToken, isInTeams }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // Show loading while checking auth status
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Authenticating...</p>
        </div>
      </div>
    );
  }

  // If we have a Teams SSO token, user is authenticated via Teams
  if (teamsSsoToken) {
    return children;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage isInTeams={isInTeams} />;
  }

  // User is authenticated, render children
  return children;
}

/**
 * Authentication Provider wrapper component
 * Wraps the app with MSAL context for Azure AD authentication
 * Requires authentication to access the app
 * Supports Teams SSO for embedded Teams apps
 */
export function AuthProvider({ children }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [msalInstance, setMsalInstance] = useState(null);
  const [initError, setInitError] = useState(null);
  const [isInTeams, setIsInTeams] = useState(false);
  const [teamsSsoToken, setTeamsSsoToken] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function initializeAuth() {
      try {
        // First, check if running in Teams
        const inTeams = await initializeTeams();
        if (isMounted) setIsInTeams(inTeams);

        if (inTeams) {
          // Running in Teams - try SSO authentication
          console.log('Teams detected, attempting SSO...');
          try {
            const token = await getTeamsSsoToken();
            if (isMounted) {
              setTeamsSsoToken(token);
              console.log('Teams SSO successful');
            }
          } catch (ssoError) {
            console.warn('Teams SSO failed, will use interactive login:', ssoError.message);
            // SSO failed - will show login button that uses Teams popup
          }
        }

        // Initialize MSAL regardless (needed for token exchange or fallback)
        if (isAzureAdConfigured()) {
          const instance = getMsalInstance();
          if (instance) {
            await instance.initialize();
            // Handle redirect promise (in case returning from login redirect)
            // Only process redirect if not in Teams (redirect doesn't work in Teams iframe)
            if (!inTeams) {
              const response = await instance.handleRedirectPromise();
              if (response) {
                console.log('Login successful:', response.account?.username);
              }
            }
            if (isMounted) setMsalInstance(instance);
          }
        } else {
          if (isMounted) setInitError('Azure AD authentication is not configured');
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        if (isMounted) setInitError(error.message);
      }

      if (isMounted) setIsInitialized(true);
    }

    // Add a timeout to prevent hanging forever
    const timeout = setTimeout(() => {
      if (isMounted && !isInitialized) {
        console.warn('Auth initialization timed out');
        setInitError('Authentication service timed out');
        setIsInitialized(true);
      }
    }, 10000);

    initializeAuth();

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, []);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if initialization failed
  if (initError && !msalInstance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Error</h2>
            <p className="text-gray-600 text-sm mb-4">{initError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If MSAL is configured and initialized, wrap with provider and require auth
  if (msalInstance) {
    return (
      <TeamsContext.Provider value={{ isInTeams, teamsSsoToken }}>
        <MsalProvider instance={msalInstance}>
          <RequireAuth teamsSsoToken={teamsSsoToken} isInTeams={isInTeams}>
            {children}
          </RequireAuth>
        </MsalProvider>
      </TeamsContext.Provider>
    );
  }

  // Fallback - should not reach here normally
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Authentication required</p>
    </div>
  );
}

export default AuthProvider;
